import { useState, useEffect } from 'react';
import { useLogicModel, Activity } from '../../../contexts/LogicModelContext';
import { supabase } from '../../../lib/supabase';
import { useTenant } from '../../../hooks/useTenant';

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
  { value: 'as-needed', label: 'As needed' },
  { value: 'one-time', label: 'One-time' },
];

export function ActivitiesStep() {
  const { logicModel, updateActivities, markStepComplete } = useLogicModel();
  const { organization } = useTenant();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    frequency: '',
    responsible_party: '',
  });

  useEffect(() => {
    if (logicModel?.activities) {
      setActivities(logicModel.activities);
    }
  }, [logicModel?.activities]);

  useEffect(() => {
    if (activities.length > 0) {
      markStepComplete('activities');
    }
  }, [activities.length, markStepComplete]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      frequency: '',
      responsible_party: '',
    });
    setEditingId(null);
    setIsAdding(false);
  };

  const handleEdit = (activity: Activity) => {
    setFormData({
      name: activity.name,
      description: activity.description || '',
      frequency: activity.frequency || '',
      responsible_party: activity.responsible_party || '',
    });
    setEditingId(activity.id || null);
    setIsAdding(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Activity name is required');
      return;
    }

    if (!logicModel?.id || !organization) return;

    setSaving(true);
    setError(null);

    try {
      const activityData = {
        name: formData.name,
        description: formData.description || null,
        frequency: formData.frequency || null,
        responsible_party: formData.responsible_party || null,
      };

      if (editingId) {
        const { error: updateError } = await supabase
          .from('activities')
          .update(activityData)
          .eq('id', editingId);

        if (updateError) throw updateError;

        const updated = activities.map(a =>
          a.id === editingId ? { ...a, ...activityData } : a
        );
        setActivities(updated);
        updateActivities(updated);
      } else {
        const { data: newActivity, error: insertError } = await supabase
          .from('activities')
          .insert({
            logic_model_id: logicModel.id,
            organization_id: organization.organization_id,
            ...activityData,
            sort_order: activities.length,
          })
          .select()
          .single();

        if (insertError) throw insertError;

        const newActivities = [...activities, newActivity];
        setActivities(newActivities);
        updateActivities(newActivities);
      }

      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (activityId: string) => {
    if (!confirm('Delete this activity?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('activities')
        .delete()
        .eq('id', activityId);

      if (deleteError) throw deleteError;

      const updated = activities.filter(a => a.id !== activityId);
      setActivities(updated);
      updateActivities(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          ⚡ Define Activities
        </h2>
        <p className="text-gray-600">
          Activities are what your program actually DOES with its resources. Each activity should produce specific outputs.
        </p>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h3 className="font-medium text-green-900 mb-2">💡 What are Activities?</h3>
        <p className="text-sm text-green-800">
          Activities are the processes, tools, events, technology, and actions that are an intentional part of your program. They're the interventions you deliver.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Activities List */}
      {activities.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-medium text-gray-900">Your Activities ({activities.length})</h3>
          {activities.map((activity, index) => (
            <div
              key={activity.id}
              className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                      {index + 1}
                    </span>
                    <h4 className="font-medium text-gray-900">{activity.name}</h4>
                  </div>
                  {activity.description && (
                    <p className="text-sm text-gray-600 mt-1 ml-8">{activity.description}</p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-2 ml-8">
                    {activity.frequency && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        🔄 {FREQUENCY_OPTIONS.find(f => f.value === activity.frequency)?.label || activity.frequency}
                      </span>
                    )}
                    {activity.responsible_party && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                        👤 {activity.responsible_party}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex space-x-2 ml-4">
                  <button
                    onClick={() => handleEdit(activity)}
                    className="text-gray-400 hover:text-blue-600"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => activity.id && handleDelete(activity.id)}
                    className="text-gray-400 hover:text-red-600"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Form */}
      {isAdding ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
          <h3 className="font-medium text-gray-900">
            {editingId ? 'Edit Activity' : 'Add Activity'}
          </h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Activity Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
              placeholder="e.g., Job readiness training workshops"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
              placeholder="What does this activity involve?"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Frequency
              </label>
              <select
                value={formData.frequency}
                onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
              >
                <option value="">Select frequency...</option>
                {FREQUENCY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Responsible Party
              </label>
              <input
                type="text"
                value={formData.responsible_party}
                onChange={(e) => setFormData({ ...formData, responsible_party: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                placeholder="e.g., Program Manager, Volunteers"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={resetForm}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50"
            >
              {saving ? 'Saving...' : editingId ? 'Update' : 'Add Activity'}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsAdding(true)}
          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-green-400 hover:text-green-600 transition-colors"
        >
          + Add Activity
        </button>
      )}

      {/* Example Activities */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-3">📋 Example Activities</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
          <span>• Job readiness training workshops</span>
          <span>• One-on-one mentorship sessions</span>
          <span>• Community outreach events</span>
          <span>• Career counseling appointments</span>
          <span>• Skills assessment testing</span>
          <span>• Resource distribution</span>
        </div>
      </div>
    </div>
  );
}
